import cx from "classnames";
import { ReactNode, useState } from "react";
import AnimateHeight from "react-animate-height";

import { Icon } from "atoms/icon/Icon";
import ChevronDown from "atoms/icon/icons/chevron_down_small.svg";
import ChevronRight from "atoms/icon/icons/chevron_right_small.svg";

export type AccordionProps = {
  title: string;
  description?: string;
  children?: ReactNode;
  disabled?: boolean;
  initiallyExpanded?: boolean;
  startIcon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  onClick?: () => void;
  titleClassName?: string;
  className?: string;
  contentClassName?: string;
  index?: number;
};

export const Accordion = ({
  title,
  description,
  children,
  disabled = false,
  initiallyExpanded = false,
  startIcon,
  onClick,
  titleClassName,
  className,
  contentClassName,
  index = 0,
}: AccordionProps) => {
  const [isExpanded, setIsExpanded] = useState(initiallyExpanded);

  const toggleExpanded = () => {
    if (disabled) return;
    setIsExpanded(!isExpanded);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === "Enter" || e.key === " ") && !disabled) {
      e.preventDefault();
      handleClick();
    }
  };

  const handleClick = () => {
    if (disabled) return;

    if (onClick) {
      onClick();
    } else if (children) {
      toggleExpanded();
    }
  };

  const hasExpandableContent = !!children;
  const contentId = `accordion-content-${index}`;
  const headerId = `accordion-header-${index}`;

  return (
    <div
      className={cx("border-b border-gray-200", className)}
      data-testid={`accordion-item-${index}`}
    >
      <button
        type="button"
        className={cx(
          "flex w-full select-none items-center justify-between px-4 py-4 pb-3 pt-4 text-left transition-colors duration-200",
          disabled && "cursor-not-allowed opacity-50"
        )}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        aria-expanded={hasExpandableContent ? isExpanded : undefined}
        aria-controls={hasExpandableContent ? contentId : undefined}
        id={headerId}
        disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        data-testid={`accordion-header-${index}`}
      >
        {startIcon && (
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-background-two/50">
            <Icon svg={startIcon} size="xsmall" color="brandColor" />
          </div>
        )}
        <div className="min-w-0 flex-1 pl-2">
          <div className={cx(titleClassName || "text-bold1 text-primary")}>
            {title}
          </div>
          {description && (
            <div className="mt-1 text-body3 text-gray-600">{description}</div>
          )}
        </div>
        {hasExpandableContent ? (
          <Icon
            svg={ChevronDown}
            size="small"
            color="primaryText"
            className={cx(
              "transition-transform duration-300 ease-in-out",
              isExpanded && "rotate-180"
            )}
          />
        ) : (
          <Icon svg={ChevronRight} size="small" color="primaryText" />
        )}
      </button>

      {hasExpandableContent && (
        <AnimateHeight
          id={contentId}
          duration={200}
          height={isExpanded ? "auto" : 0}
          easing="ease-in-out"
          aria-labelledby={headerId}
          data-testid={`accordion-content-${index}`}
        >
          <div className={cx("select-none px-6 pb-4", contentClassName)}>
            {children}
          </div>
        </AnimateHeight>
      )}
    </div>
  );
};
